<template>
  <div class="flex flex-col h-[calc(90vh)]">
    <!-- Скрытый сканер -->
    <QRTextInput
      ref="qrInput"
      v-model="scanValue"
      class="hidden-input"
      :auto-clear="false"
      @scan-complete="handleScan"
      @focus="isInputFocused = true"
      @blur="isInputFocused = false"
    />

    <!-- Навигация, сохранение и печать -->
    <div class="bg-white border-b px-4 py-2 flex items-center justify-between">
      <button
        @click="goBack"
        class="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
      >
        <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
        </svg>
        Назад к операции
      </button>

      <div class="flex items-center gap-2">
        <button
          v-can-write
          @click="save"
          :disabled="!isDirty || isSaving"
          class="px-4 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {{ isSaving ? 'Сохранение...' : 'Сохранить' }}
        </button>

        <div class="relative" ref="printMenuRef">
          <button
            @click="showPrintMenu = !showPrintMenu"
            class="px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 text-sm inline-flex items-center gap-1"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
            Печать
            <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          <div v-if="showPrintMenu" class="absolute right-0 mt-1 w-64 bg-white border rounded-lg shadow-lg z-20">
            <button @click="printDocument('order')" class="w-full text-left px-4 py-2 text-sm hover:bg-gray-100">Упаковочный лист заказа</button>
            <button @click="printDocument('place')" class="w-full text-left px-4 py-2 text-sm hover:bg-gray-100">Упаковочные листы по местам</button>
            <button @click="printDocument('labels')" class="w-full text-left px-4 py-2 text-sm hover:bg-gray-100">Транспортные этикетки</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Основной контент: split-панель -->
    <div class="flex-1 flex overflow-hidden min-h-0">
      <!-- Левая панель: материалы заказа -->
      <div class="w-1/2 border-r flex flex-col bg-white overflow-hidden">
        <div class="px-4 py-3 bg-gray-50 border-b flex items-center justify-between">
          <h2 class="font-semibold text-sm uppercase tracking-wide text-gray-700">Материалы заказа</h2>
          <span
            class="text-xs font-semibold cursor-pointer"
            :class="isInputFocused ? 'text-gray-500' : 'text-red-500'"
            @click.stop="focusScanner"
          >
            {{ isInputFocused ? 'Ожидается сканирование' : 'Нажмите для сканирования' }}
          </span>
        </div>
        <div class="flex-1 overflow-y-auto p-3 space-y-1">
          <div v-if="loading" class="text-center py-8 text-gray-400">Загрузка...</div>
          <template v-for="material in computedMaterials" :key="material.product_id">
            <!-- Карточка материала -->
            <div
              class="rounded border text-sm"
              :class="getMaterialClass(material)"
            >
              <!-- Заголовок карточки: название + X/Y/Z -->
              <div class="flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-blue-50 transition-colors"
                   @click="addFromHeader(material)">
                <div class="flex-1 min-w-0">
                  <div class="truncate font-medium">{{ material.product_name }}</div>
                  <div class="text-xs text-gray-500">{{ material.sku }}</div>
                </div>
                <div class="ml-2 text-right whitespace-nowrap">
                  <span
                    :class="getMaterialCountClass(material)"
                    v-tooltip="'Упаковано / Скомплектовано / По спецификации'"
                  >
                    {{ material.distributed_quantity }} / {{ material.picked_quantity }} / {{ material.required_quantity }}
                  </span>
                  <span class="text-xs text-gray-400 ml-1">{{ material.unit }}</span>
                </div>
              </div>
              <!-- Строки внутри карточки — только если есть серийные -->
              <div v-if="getSerialItems(material).length > 0" class="border-t divide-y">
                <!-- Количественные позиции — только если есть и серийные и количественные -->
                <div
                  v-for="(item, idx) in getQuantityItems(material)"
                  :key="material.product_id + '_q_' + idx"
                  class="flex items-center justify-between px-3 py-1.5 cursor-pointer hover:bg-blue-50 transition-colors"
                  @click="addMaterialToSelectedPlace(material)"
                >
                  <span class="text-xs text-gray-600">{{ item.count }} {{ material.unit }}</span>
                  <span class="text-xs" :class="isQuantityItemDistributed(material) ? 'text-green-600' : 'text-gray-400'">
                    {{ isQuantityItemDistributed(material) ? '✓' : '' }}
                  </span>
                </div>
                <!-- Серийные позиции -->
                <div
                  v-for="(item, idx) in getSerialItems(material)"
                  :key="material.product_id + '_s_' + idx"
                  class="flex items-center justify-between px-3 py-1.5 cursor-pointer hover:bg-blue-50 transition-colors"
                  @click="addSerialItemToSelectedPlace(material, item)"
                >
                  <span class="text-xs text-blue-600">S/N: {{ item.serial_number || '—' }}</span>
                  <span class="text-xs" :class="isSerialItemDistributed(item) ? 'text-green-600' : 'text-gray-400'">
                    {{ isSerialItemDistributed(item) ? '✓' : '' }}
                  </span>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>

      <!-- Правая панель: упаковочные места -->
      <div class="w-1/2 flex flex-col bg-gray-50">
        <div class="px-4 py-2 bg-gray-50 border-b flex items-center justify-between">
          <h2 class="font-semibold text-sm uppercase tracking-wide text-gray-700">Упаковочные места</h2>
          <button
            v-can-write
            @click.stop="createPlace"
            class="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
          >
            + Добавить место
          </button>
        </div>
        <div class="flex-1 overflow-y-auto p-3 space-y-3">
          <PackingPlaceCard
            v-for="place in places"
            :key="place._localId"
            :place="place"
            :is-selected="selectedPlaceId === place._localId"
            :total-places="places.length"
            @select="selectedPlaceId = $event"
            @delete="deletePlace"
            @remove-item="removeItem"
            @move-item="openMoveDialog"
            @update-place="updatePlaceLocal"
            @focus-scanner="focusScanner"
          />
        </div>
      </div>
    </div>

    <!-- Прогресс-бар -->
    <div class="bg-white border-t px-4 py-1.5">
      <div class="flex items-center justify-between mb-1">
        <span class="text-sm font-medium text-gray-700">
          Распределено: {{ distributedCount }} из {{ totalCount }}
        </span>
        <span class="text-sm text-gray-500">{{ progressPercent }}%</span>
      </div>
      <div class="w-full bg-gray-200 rounded-full h-2">
        <div
          class="h-2 rounded-full transition-all duration-300"
          :class="progressPercent >= 100 ? 'bg-green-500' : 'bg-blue-500'"
          :style="{ width: progressPercent + '%' }"
        ></div>
      </div>
    </div>

    <!-- Модальное окно перемещения -->
    <div v-if="moveDialog.show" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg shadow-xl p-6 w-80">
        <h3 class="text-lg font-semibold mb-4">Переместить в место</h3>
        <div class="space-y-2">
          <button
            v-for="place in places.filter(p => p._localId !== moveDialog.fromPlaceLocalId)"
            :key="place._localId"
            @click="confirmMove(place._localId)"
            class="w-full text-left px-4 py-2 border rounded hover:bg-blue-50 text-sm"
          >
            Место #{{ place.number }}
          </button>
        </div>
        <button
          @click="moveDialog.show = false"
          class="mt-4 w-full px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 text-sm"
        >
          Отмена
        </button>
      </div>
    </div>

    <!-- Модальное окно подтверждения скана -->
    <div v-if="scanConfirm.show" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg shadow-xl p-6 w-96">
        <h3 class="text-lg font-semibold mb-2">Добавить в Место #{{ getSelectedPlaceNumber() }}?</h3>
        <div class="bg-gray-50 rounded p-3 mb-4">
          <div class="font-medium">{{ scanConfirm.product_name }}</div>
          <div class="text-sm text-gray-500">{{ scanConfirm.sku }}</div>
          <div v-if="scanConfirm.serial_number" class="text-sm text-blue-600">S/N: {{ scanConfirm.serial_number }}</div>
          <div v-if="!scanConfirm.batch_product_id" class="text-sm mt-1">Количество: {{ scanConfirm.quantity }}</div>
        </div>
        <div class="flex gap-2">
          <button
            @click="confirmScanAdd"
            class="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
          >
            Добавить
          </button>
          <button
            @click="scanConfirm.show = false; focusScanner()"
            class="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 text-sm"
          >
            Отмена
          </button>
        </div>
      </div>
    </div>

    <!-- Модалка ввода количества -->
    <div v-if="quantityModal.show" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg shadow-xl p-6 w-80">
        <h3 class="text-lg font-semibold mb-2">Количество</h3>
        <p class="text-sm text-gray-600 mb-3">{{ quantityModal.productName }}</p>
        <input
          ref="quantityInput"
          type="number"
          step="0.001"
          v-model.number="quantityModal.value"
          @keydown.enter="confirmQuantity"
          class="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mb-4"
        />
        <div class="flex gap-2">
          <button @click="confirmQuantity" class="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm">Добавить</button>
          <button @click="quantityModal.show = false" class="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 text-sm">Отмена</button>
        </div>
      </div>
    </div>

    <!-- Модалка подтверждения удаления места -->
    <div v-if="deleteConfirm.show" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg shadow-xl p-6 w-80">
        <h3 class="text-lg font-semibold mb-2">Удалить Место #{{ deleteConfirm.number }}?</h3>
        <p class="text-sm text-gray-600 mb-4">В этом месте {{ deleteConfirm.itemCount }} позиций. Они будут убраны.</p>
        <div class="flex gap-2">
          <button @click="confirmDelete" class="flex-1 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm">Удалить</button>
          <button @click="deleteConfirm.show = false" class="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 text-sm">Отмена</button>
        </div>
      </div>
    </div>

    <!-- Модалка несохранённых изменений -->
    <div v-if="exitConfirm.show" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg shadow-xl p-6 w-80">
        <h3 class="text-lg font-semibold mb-2">Несохранённые изменения</h3>
        <p class="text-sm text-gray-600 mb-4">Выйти без сохранения?</p>
        <div class="flex gap-2">
          <button @click="exitConfirm.show = false; $emit('close')" class="flex-1 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm">Выйти</button>
          <button @click="exitConfirm.show = false" class="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 text-sm">Остаться</button>
        </div>
      </div>
    </div>

    <!-- Модальное окно подтверждения переноса -->
    <div v-if="scanMoveConfirm.show" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg shadow-xl p-6 w-96">
        <h3 class="text-lg font-semibold mb-2">Переместить в Место #{{ scanMoveConfirm.toNumber }}?</h3>
        <div class="bg-gray-50 rounded p-3 mb-4">
          <div class="font-medium">{{ scanMoveConfirm.product_name }}</div>
          <div v-if="scanMoveConfirm.serial_number" class="text-sm text-blue-600">S/N: {{ scanMoveConfirm.serial_number }}</div>
          <div class="text-sm text-gray-500 mt-1">Сейчас в Месте #{{ scanMoveConfirm.fromNumber }}</div>
        </div>
        <div class="flex gap-2">
          <button
            @click="confirmScanMove"
            class="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
          >
            Переместить
          </button>
          <button
            @click="scanMoveConfirm.show = false; focusScanner()"
            class="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 text-sm"
          >
            Отмена
          </button>
        </div>
      </div>
    </div>

    <!-- Тост ошибки/предупреждения -->
    <div v-if="toastMessage" class="fixed top-4 right-4 px-4 py-2 rounded-lg shadow-lg z-50 text-sm text-white" :class="toastType === 'error' ? 'bg-red-500' : 'bg-yellow-500'">
      {{ toastMessage }}
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import QRTextInput from '@/components/QRTextInput.vue';
import PackingPlaceCard from '@/components/production/PackingPlaceCard.vue';

let localIdCounter = 0;

export default {
  name: 'PackingPlaces',
  components: { QRTextInput, PackingPlaceCard },
  props: {
    taskId: { type: [String, Number], required: true },
  },
  emits: ['close'],
  data() {
    return {
      loading: false,
      isSaving: false,
      isDirty: false,
      isInputFocused: false,
      scanValue: '',
      scanError: '',
      materials: [],
      places: [],
      selectedPlaceId: null,
      showPrintMenu: false,
      moveDialog: { show: false, itemLocalId: null, fromPlaceLocalId: null },
      scanConfirm: { show: false, product_id: null, product_name: '', sku: '', quantity: 1, serial_number: '', batch_product_id: null, storehouse_product_id: null },
      scanMoveConfirm: { show: false, product_name: '', serial_number: '', fromNumber: 0, toNumber: 0, bpId: null, sourceLocalId: null },
      quantityModal: { show: false, value: 0, productName: '', material: null },
      deleteConfirm: { show: false, localId: null, number: 0, itemCount: 0 },
      exitConfirm: { show: false },
      toastMessage: '',
      toastType: 'error',
    };
  },
  computed: {
    computedMaterials() {
      const distributed = {};
      const distributedBp = {};
      for (const place of this.places) {
        for (const item of place.items || []) {
          distributed[item.product_id] = (distributed[item.product_id] || 0) + parseFloat(item.quantity);
          if (item.batch_product_id) {
            distributedBp[item.batch_product_id] = true;
          }
        }
      }
      return this.materials.map(m => ({
        ...m,
        distributed_quantity: distributed[m.product_id] || 0,
        _distributedBp: distributedBp,
      }));
    },
    totalCount() {
      // Счётчик распределения считается по плану упаковочной спецификации
      return this.materials.reduce((sum, m) => sum + parseFloat(m.required_quantity || 0), 0);
    },
    distributedCount() {
      return this.computedMaterials.reduce((sum, m) => sum + m.distributed_quantity, 0);
    },
    progressPercent() {
      if (this.totalCount === 0) return 0;
      return Math.min(100, Math.round((this.distributedCount / this.totalCount) * 100));
    },
  },
  mounted() {
    this.loadData();
    document.addEventListener('click', this.closePrintMenu);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.closePrintMenu);
  },
  methods: {
    nextLocalId() {
      return '_local_' + (++localIdCounter);
    },

    focusScanner() {
      this.$nextTick(() => {
        if (this.$refs.qrInput) this.$refs.qrInput.forceFocus();
      });
    },

    async loadData() {
      this.loading = true;
      try {
        const [materialsRes, placesRes] = await Promise.all([
          axios.post('/api/packingPlace/getOrderMaterials', { production_task_id: this.taskId }),
          axios.post('/api/packingPlace/getPackingPlaces', { production_task_id: this.taskId }),
        ]);

        this.materials = materialsRes.data.data;
        this.places = (placesRes.data.data || []).map(place => ({
          ...place,
          _localId: place.id ? 'server_' + place.id : this.nextLocalId(),
          items: (place.items || []).map(item => ({
            ...item,
            _localId: item.id ? 'server_' + item.id : this.nextLocalId(),
          })),
        }));

        if (this.places.length === 0) {
          this.createPlace();
          this.isDirty = false;
        }

        if (this.places.length > 0 && !this.selectedPlaceId) {
          this.selectedPlaceId = this.places[0]._localId;
        }

        this.$nextTick(() => this.focusScanner());
      } catch (error) {
        console.error('Ошибка загрузки данных:', error);
      } finally {
        this.loading = false;
      }
    },

    // --- Helpers для материалов ---

    getSerialItems(material) {
      return (material.picked_items || []).filter(i => i.is_single && i.serial_number);
    },
    getMaterialClass(material) {
      // Позиция считается распределённой по плану упаковочной спецификации
      if (material.distributed_quantity >= material.required_quantity) return 'bg-green-50 border-green-200';
      if (!material.is_picked) return 'bg-red-50 border-red-200';
      return 'bg-white border-gray-200';
    },
    getMaterialCountClass(material) {
      // Позиция считается распределённой по плану упаковочной спецификации
      if (material.distributed_quantity >= material.required_quantity) return 'text-green-600';
      if (!material.is_picked) return 'text-red-600';
      return 'text-gray-600';
    },
    getItemClass(material, item) {
      if (this.isSerialItemDistributed(item)) return 'bg-green-50 border-green-200';
      if (!material.is_picked) return 'bg-red-50 border-red-200';
      return 'bg-white border-gray-200';
    },
    isSerialItemDistributed(item) {
      for (const place of this.places) {
        if ((place.items || []).some(i => i.batch_product_id && i.batch_product_id === item.batch_product_id)) return true;
      }
      return false;
    },
    // Найти picked_item по данным скана (локально)
    findPickedItemByScan(scanData) {
      for (const material of this.materials) {
        for (const pi of material.picked_items || []) {
          if (scanData.spid && pi.storehouse_product_id == scanData.spid) return { material, pickedItem: pi };
          if (scanData.bpid && pi.batch_product_id == scanData.bpid) return { material, pickedItem: pi };
        }
      }
      return null;
    },

    // Количественные picked_items (не серийные)
    getQuantityItems(material) {
      return (material.picked_items || []).filter(i => !i.is_single || !i.serial_number);
    },

    // Проверка: количественная позиция распределена (упрощённо — если общий distributed >= picked для количественной части)
    isQuantityItemDistributed(material) {
      return this.getQuantityDistributed(material) >= this.getQuantityPicked(material);
    },

    // Добавить или склеить item в место — склеиваем только количественные (без batch_product_id)
    addOrMergeItem(place, newItem) {
      if (!newItem.batch_product_id) {
        const existing = place.items.find(i => i.product_id === newItem.product_id && !i.batch_product_id);
        if (existing) {
          existing.quantity = parseFloat(existing.quantity) + parseFloat(newItem.quantity);
          this.isDirty = true;
          return;
        }
      }
      place.items.push(newItem);
      this.isDirty = true;
    },

    // Количество серийных единиц для материала
    getSerialCount(material) {
      return this.getSerialItems(material).reduce((sum, i) => sum + parseFloat(i.count || 1), 0);
    },

    // Количественная часть: план спецификации без серийных
    getQuantityTotal(material) {
      return Math.max(0, parseFloat(material.required_quantity || 0) - this.getSerialCount(material));
    },

    // Количественная часть picked: picked без серийных
    getQuantityPicked(material) {
      const serialPicked = this.getSerialItems(material).reduce((sum, i) => sum + parseFloat(i.count || 1), 0);
      return parseFloat(material.picked_quantity || 0) - serialPicked;
    },

    // Количество без хвостовых нулей на целых: 5, 2.5
    formatQuantity(val) {
      const num = Number(val);
      return isNaN(num) ? '0' : (Math.round(num * 1000) / 1000).toString();
    },

    // Количественная часть distributed: distributed без серийных
    getQuantityDistributed(material) {
      let total = material.distributed_quantity || 0;
      // Вычитаем серийные которые уже распределены
      for (const item of this.getSerialItems(material)) {
        if (this.isSerialItemDistributed(item)) {
          total -= parseFloat(item.count || 1);
        }
      }
      return Math.max(0, total);
    },

    // --- Локальные операции ---

    createPlace() {
      const maxNumber = this.places.reduce((max, p) => Math.max(max, p.number || 0), 0);
      const newPlace = {
        _localId: this.nextLocalId(), id: null, number: maxNumber + 1,
        dimensions: null, weight_gross: null, items: [],
      };
      this.places.push(newPlace);
      this.selectedPlaceId = newPlace._localId;
      this.isDirty = true;
      this.focusScanner();
    },

    deletePlace(localId) {
      if (this.places.length <= 1) { this.showToast('Нельзя удалить последнее место', 'error'); return; }
      const place = this.places.find(p => p._localId === localId);
      if (!place) return;

      // Если место пустое — удаляем сразу без подтверждения
      if (!place.items || place.items.length === 0) {
        this.doDeletePlace(localId);
        return;
      }

      // Если есть позиции — модалка подтверждения
      this.deleteConfirm = { show: true, localId, number: place.number, itemCount: place.items.length };
    },

    confirmDelete() {
      this.doDeletePlace(this.deleteConfirm.localId);
      this.deleteConfirm.show = false;
    },

    doDeletePlace(localId) {
      this.places = this.places.filter(p => p._localId !== localId);
      this.places.forEach((p, i) => { p.number = i + 1; });
      if (this.selectedPlaceId === localId) this.selectedPlaceId = this.places[0]?._localId || null;
      this.isDirty = true;
    },

    // Клик по шапке карточки: если нет под-строк — добавить количественную, если есть — добавить первую нераспределённую
    addFromHeader(material) {
      const serialItems = this.getSerialItems(material);
      if (serialItems.length === 0) {
        // Нет серийных — добавляем количественную часть
        this.addMaterialToSelectedPlace(material);
        return;
      }
      // Есть под-строки — ищем первую нераспределённую серийную
      const undistributedSerial = serialItems.find(item => !this.isSerialItemDistributed(item));
      if (undistributedSerial) {
        this.addSerialItemToSelectedPlace(material, undistributedSerial);
        return;
      }
      // Все серийные распределены — добавляем количественную часть если осталось
      this.addMaterialToSelectedPlace(material);
    },

    addMaterialToSelectedPlace(material) {
      if (!this.selectedPlaceId) { this.showToast('Выберите упаковочное место', 'error'); return; }
      const qtyTotal = this.getQuantityTotal(material);
      const qtyDistributed = this.getQuantityDistributed(material);
      // Потолок — план упаковочной спецификации: излишек комплектования не раскладывается,
      // поэтому объясняем упаковщику, почему добавление ничего не сделало
      if (qtyDistributed >= qtyTotal) {
        this.showToast(
          'Позиция распределена полностью: по спецификации нужно ' + this.formatQuantity(material.required_quantity) + ' ' + material.unit,
          'info'
        );
        return;
      }

      const remaining = qtyTotal - qtyDistributed;
      if (remaining <= 1) {
        this.doAddMaterial(material, remaining);
      } else {
        this.quantityModal = { show: true, value: remaining, productName: material.product_name, material };
        this.$nextTick(() => { if (this.$refs.quantityInput) this.$refs.quantityInput.select(); });
      }
    },

    confirmQuantity() {
      const qty = parseFloat(this.quantityModal.value);
      if (qty <= 0) { this.quantityModal.show = false; return; }
      this.doAddMaterial(this.quantityModal.material, qty);
      this.quantityModal.show = false;
    },

    doAddMaterial(material, quantity) {
      const place = this.places.find(p => p._localId === this.selectedPlaceId);
      if (!place) return;
      const remaining = this.getQuantityTotal(material) - this.getQuantityDistributed(material);

      this.addOrMergeItem(place, {
        _localId: this.nextLocalId(), id: null,
        product_id: material.product_id,
        product: { id: material.product_id, name: material.product_name, sku: material.sku },
        quantity: Math.min(quantity, remaining),
        batch_product_id: null, storehouse_product_id: null,
      });
    },

    addSerialItemToSelectedPlace(material, item) {
      if (!this.selectedPlaceId) { this.showToast('Выберите упаковочное место', 'error'); return; }
      if (this.isSerialItemDistributed(item)) return;

      const place = this.places.find(p => p._localId === this.selectedPlaceId);
      if (!place) return;

      this.addOrMergeItem(place, {
        _localId: this.nextLocalId(), id: null,
        product_id: material.product_id,
        product: { id: material.product_id, name: material.product_name, sku: material.sku },
        quantity: item.count || 1,
        batch_product_id: item.batch_product_id,
        storehouse_product_id: item.storehouse_product_id,
        serial_number: item.serial_number || null,
      });
      this.isDirty = true;
    },

    removeItem(itemLocalId) {
      for (const place of this.places) {
        const idx = place.items.findIndex(i => i._localId === itemLocalId);
        if (idx !== -1) { place.items.splice(idx, 1); this.isDirty = true; return; }
      }
    },

    openMoveDialog(itemLocalId) {
      let fromPlaceLocalId = null;
      for (const place of this.places) {
        if (place.items.some(i => i._localId === itemLocalId)) { fromPlaceLocalId = place._localId; break; }
      }
      this.moveDialog = { show: true, itemLocalId, fromPlaceLocalId };
    },

    confirmMove(targetPlaceLocalId) {
      const { itemLocalId } = this.moveDialog;
      let item = null;
      for (const place of this.places) {
        const idx = place.items.findIndex(i => i._localId === itemLocalId);
        if (idx !== -1) { item = place.items.splice(idx, 1)[0]; break; }
      }
      if (item) {
        const targetPlace = this.places.find(p => p._localId === targetPlaceLocalId);
        if (targetPlace) { this.addOrMergeItem(targetPlace, item); }
      }
      this.moveDialog.show = false;
    },

    confirmScanMove() {
      const { bpId, sourceLocalId } = this.scanMoveConfirm;
      const sourcePlace = this.places.find(p => p._localId === sourceLocalId);
      if (sourcePlace) {
        const itemIdx = sourcePlace.items.findIndex(i => i.batch_product_id === bpId);
        if (itemIdx !== -1) {
          const [item] = sourcePlace.items.splice(itemIdx, 1);
          const targetPlace = this.places.find(p => p._localId === this.selectedPlaceId);
          if (targetPlace) {
            targetPlace.items.push(item);
            this.isDirty = true;
          }
        }
      }
      this.scanMoveConfirm.show = false;
      this.focusScanner();
    },

    updatePlaceLocal(data) {
      const place = this.places.find(p => p._localId === data._localId || p.id === data.packing_place_id);
      if (!place) return;
      if ('dimensions' in data) place.dimensions = data.dimensions;
      if ('weight_gross' in data) place.weight_gross = data.weight_gross;
      this.isDirty = true;
    },

    // --- Сканирование ---

    parseQRData(scannedData) {
      try { return JSON.parse(scannedData); } catch {}
      const params = {};
      scannedData.split('&').forEach(part => {
        const [key, val] = part.split('=');
        if (key && val) params[key.toLowerCase()] = val;
      });
      return params;
    },

    handleScan(value) {
      if (!value || !this.selectedPlaceId) return;
      const parsed = this.parseQRData(value);
      const scanData = {};
      if (parsed.spid) scanData.spid = parsed.spid;
      else if (parsed.bpid || parsed.pid) scanData.bpid = parsed.bpid || parsed.pid;
      else { this.showScanError('Неопознанный формат QR-кода'); this.clearScan(); return; }

      // Локальная проверка среди picked_items
      const found = this.findPickedItemByScan(scanData);
      if (!found) {
        this.showScanError('Продукция не найдена среди скомплектованных');
        this.clearScan();
        return;
      }

      // Серийная позиция уже распределена — предлагаем перенести или игнорируем
      if (found.pickedItem.batch_product_id && this.isSerialItemDistributed(found.pickedItem)) {
        const bpId = found.pickedItem.batch_product_id;
        const sourcePlace = this.places.find(p => (p.items || []).some(i => i.batch_product_id === bpId));
        if (sourcePlace && sourcePlace._localId !== this.selectedPlaceId) {
          const targetPlace = this.places.find(p => p._localId === this.selectedPlaceId);
          this.scanMoveConfirm = {
            show: true,
            product_name: found.material.product_name,
            serial_number: found.pickedItem.serial_number || '',
            fromNumber: sourcePlace.number,
            toNumber: targetPlace?.number || '?',
            bpId,
            sourceLocalId: sourcePlace._localId,
          };
        }
        this.clearScan();
        return;
      }

      this.scanConfirm = {
        show: true,
        product_id: found.material.product_id,
        product_name: found.material.product_name,
        sku: found.material.sku,
        quantity: found.pickedItem.count || 1,
        serial_number: found.pickedItem.serial_number || '',
        batch_product_id: found.pickedItem.batch_product_id,
        storehouse_product_id: found.pickedItem.storehouse_product_id,
      };
      this.clearScan();
    },

    confirmScanAdd() {
      const place = this.places.find(p => p._localId === this.selectedPlaceId);
      if (!place) return;

      this.addOrMergeItem(place, {
        _localId: this.nextLocalId(), id: null,
        product_id: this.scanConfirm.product_id,
        product: { id: this.scanConfirm.product_id, name: this.scanConfirm.product_name, sku: this.scanConfirm.sku },
        quantity: this.scanConfirm.quantity,
        batch_product_id: this.scanConfirm.batch_product_id,
        storehouse_product_id: this.scanConfirm.storehouse_product_id,
        serial_number: this.scanConfirm.serial_number || null,
      });
      this.scanConfirm.show = false;
      this.focusScanner();
    },

    getSelectedPlaceNumber() {
      const place = this.places.find(p => p._localId === this.selectedPlaceId);
      return place?.number || '?';
    },

    clearScan() {
      this.scanValue = '';
      if (this.$refs.qrInput) this.$refs.qrInput.clear();
    },

    showToast(msg, type = 'error') {
      this.toastMessage = msg;
      this.toastType = type;
      setTimeout(() => { this.toastMessage = ''; }, 3000);
    },

    showScanError(msg) {
      this.showToast(msg, 'error');
    },

    // --- Сохранение ---

    async save() {
      this.isSaving = true;
      try {
        const payload = {
          production_task_id: this.taskId,
          places: this.places.map(place => ({
            id: place.id || null,
            dimensions: place.dimensions,
            weight_gross: place.weight_gross,
            items: (place.items || []).map(item => ({
              id: item.id || null,
              product_id: item.product_id,
              quantity: item.quantity,
              batch_product_id: item.batch_product_id || null,
              storehouse_product_id: item.storehouse_product_id || null,
            })),
          })),
        };
        await axios.post('/api/packingPlace/syncPackingPlaces', payload);
        await this.loadData();
        this.isDirty = false;
      } catch (error) {
        console.error('Ошибка сохранения:', error);
        alert('Ошибка сохранения: ' + (error.response?.data?.message || error.message));
      } finally {
        this.isSaving = false;
      }
    },

    // --- Навигация ---

    goBack() {
      if (this.isDirty) {
        this.exitConfirm.show = true;
        return;
      }
      this.$emit('close');
    },

    closePrintMenu(e) {
      if (this.$refs.printMenuRef && !this.$refs.printMenuRef.contains(e.target)) this.showPrintMenu = false;
    },

    async printDocument(type) {
      if (this.isDirty) await this.save();
      this.showPrintMenu = false;
      const urls = {
        order: '/api/packingPlace/printPackingListOrder',
        place: '/api/packingPlace/printPackingListPlace',
        labels: '/api/packingPlace/printTransportLabels',
      };
      try {
        const response = await axios.get(urls[type], { params: { production_task_id: this.taskId } });
        const printWindow = window.open('', '_blank');
        if (!printWindow) { alert('Разрешите всплывающие окна'); return; }
        printWindow.document.write(response.data);
        printWindow.document.close();
        printWindow.onload = () => {
          setTimeout(() => { printWindow.print(); }, 500);
        };
      } catch (error) {
        alert('Ошибка печати: ' + (error.response?.data?.message || error.message));
      }
    },
  },
};
</script>

<style scoped>
.hidden-input {
  position: absolute !important;
  top: -9999px !important;
  left: -9999px !important;
  pointer-events: none !important;
}
</style>
